import React, { useState, useMemo, useImperativeHandle } from 'react';
import { Form, Input, message, Space } from 'antd';
import AdvanceFilterBuilder from './AdvanceFilterBuilder';
import Category from './Category';
import { get } from 'lodash';
import HideFilter from './HideFilter';
import { DataSource, isInvalidXMLString } from '@components/filter/components/utils';
import { getMatchedCategory, getCategoryOptions } from './utils';
import * as Styled from './styled';
import * as Types from '@components/filter/types';
import {
  type Query,
  queryValidator,
} from '@components/filter/components/common/FilterRulesBuilder';

export interface FormValues {
  name?: string;
  query?: Query;
  category?: string;
  isHiddenFromCustomer?: boolean;
}

export interface Props {
  initialValues?: FormValues;
  disableFilterBuilder?: boolean;
  disableHiddenFilter?: boolean;
  filterMeta: Types.FilterMeta;
  dataSource: DataSource;
  onChange?: (changedValue: Partial<FormValues>, values: FormValues) => void;
}

export interface AdvanceFilterBuilderFormRef {
  validateFields: (fields?: string[]) => Promise<FormValues | undefined>;
  getValue: () => FormValues;
}

const processFormData = (formData: FormValues, { disableFilterBuilder, disableHiddenFilter }) => {
  if (disableFilterBuilder) {
    delete formData.query;
  }
  if (disableHiddenFilter) {
    delete formData.isHiddenFromCustomer;
  }
  return formData;
};

const AdvanceFilterBuilderForm = React.forwardRef<AdvanceFilterBuilderFormRef, Props>(
  (props, ref) => {
    const {
      filterMeta,
      dataSource,
      initialValues,
      disableFilterBuilder = false,
      disableHiddenFilter = false,
      onChange,
    } = props;

    const formItemStyle = useMemo(() => {
      return {
        paddingLeft: disableFilterBuilder ? '0px' : '60px',
        marginBottom: disableFilterBuilder ? '0px' : '12px',
        paddingBottom: disableFilterBuilder ? '6px' : undefined,
      };
    }, [disableFilterBuilder]);

    const [form] = Form.useForm<FormValues>();

    const [showFilterBuilderError, setShowFilterBuilderError] = useState(false);

    const categoryOptions = useMemo(() => getCategoryOptions(filterMeta), [filterMeta]);

    useImperativeHandle(
      ref,
      () => {
        return {
          getValue: () =>
            processFormData(form.getFieldsValue(), {
              disableFilterBuilder,
              disableHiddenFilter,
            }),
          validateFields: async (fields) => {
            let hasFormError = false;
            try {
              await form?.validateFields(fields);
            } catch (e) {
              const firstErrorField = get(e, 'errorFields[0].name[0]');
              if (firstErrorField) {
                form.scrollToField(firstErrorField);
              }
              hasFormError = true;
            }

            // Special check for query
            let isValidQuery = true;
            if (!disableFilterBuilder) {
              const values = form.getFieldsValue();
              isValidQuery = queryValidator(values.query);
              if (!isValidQuery) {
                setShowFilterBuilderError(true);
                if (!values.query?.rules?.length) {
                  message.warning('Please add filter rules');
                }
                const firstErrorInput = document.querySelector(
                  '.filter-builder-form-container .error-input',
                );
                firstErrorInput?.scrollIntoView({ block: 'nearest' });
              } else {
                setShowFilterBuilderError(false);
              }
            }

            if (!hasFormError && isValidQuery) {
              return processFormData(form.getFieldsValue(), {
                disableFilterBuilder,
                disableHiddenFilter,
              });
            }

            return;
          },
        };
      },
      [form, disableFilterBuilder, disableHiddenFilter],
    );

    return (
      <Styled.FormContainer
        className={`filter-builder-form-container ${disableFilterBuilder ? 'disable-filter-builder' : ''}`}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          validateTrigger={false}
          onFieldsChange={(target) => {
            const newQuery = target.find((it) => it.name && it.name[0] === 'query')?.value;
            if (newQuery) {
              setShowFilterBuilderError(false);
              const newCategory = getMatchedCategory(
                get(newQuery, 'rules[0].field') ?? get(newQuery, 'rules[0].rules[0].field'),
                filterMeta,
                categoryOptions,
              );
              form.setFieldValue('category', newCategory);
            }
          }}
          onValuesChange={onChange}
        >
          {!disableFilterBuilder && (
            <Form.Item name="query">
              <AdvanceFilterBuilder
                showError={showFilterBuilderError}
                dataSource={dataSource}
                filterMeta={filterMeta}
              />
            </Form.Item>
          )}
          <Form.Item style={{ ...formItemStyle, marginBottom: '0px' }} rootClassName="divider">
            <Styled.Tips>If you wish to save this filter, enter these details</Styled.Tips>
          </Form.Item>
          <Space direction={disableFilterBuilder ? 'vertical' : 'horizontal'} align="start">
            <Form.Item
              name="name"
              label="Filter Name"
              style={formItemStyle}
              rules={[
                () => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject('Filter name is required');
                    }
                    if (!/^[0-9A-Za-z]/.test(value)) {
                      return Promise.reject(new Error('The first character must be alphanumeric'));
                    }
                    if (isInvalidXMLString(value)) {
                      return Promise.reject(
                        new Error(
                          `Filter name '${value}' has invalid characters which are not supported.`,
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                autoComplete="off"
                maxLength={128}
                placeholder="Please enter filter name"
                style={{ width: '300px' }}
              />
            </Form.Item>
            <Form.Item name="category" style={{ marginBottom: '12px' }} label="Category">
              <Category categoryOptions={categoryOptions} />
            </Form.Item>
          </Space>
          {!disableHiddenFilter && (
            <Form.Item name="isHiddenFromCustomer" style={formItemStyle}>
              <HideFilter />
            </Form.Item>
          )}
        </Form>
      </Styled.FormContainer>
    );
  },
);

AdvanceFilterBuilderForm.displayName = 'AdvanceFilterBuilderForm';

export default React.memo(AdvanceFilterBuilderForm);
